import { sumMeasured } from "../derive";
import { downsample, TrendPoint, TrendSeries } from "../trendSeries";

interface iSparklineProps {
    series?: TrendSeries;
    width?: number;
    height?: number;
}

const DEFAULT_WIDTH = 120;
const DEFAULT_HEIGHT = 20;

/**
 * Comfortably under the 120px drawing width, per `downsample`'s own
 * contract: a line reads as continuous well below one point per pixel, so
 * the target should sit under the pixel width, not equal to it.
 */
const DRAW_POINTS = 48;

type PickValue = (point: TrendPoint) => number | undefined;

/**
 * Builds one path, starting a new segment after any gap.
 *
 * A gap must break the line, never dip to zero. A dip would claim no mail
 * flowed during that interval, which is a measurement the collector did not
 * make — the same class of lie as rendering an absent count as 0.
 *
 * Takes `readonly TrendPoint[]` because the caller passes `downsample`'s
 * result straight through — this only ever reads it.
 */
function linePath(
    points: readonly TrendPoint[],
    pick: PickValue,
    x: (at: number) => number,
    y: (v: number) => number,
): string {
    const parts: string[] = [];
    let pen = false;

    for (const point of points) {
        const value = pick(point);
        if (value === undefined) {
            pen = false;
            continue;
        }
        parts.push(`${pen ? "L" : "M"}${x(point.at).toFixed(1)} ${y(value).toFixed(1)}`);
        pen = true;
    }

    return parts.join(" ");
}

/**
 * A tenant's 24-hour throughput, in and out, at table-cell size.
 *
 * Deliberately NOT Recharts, unlike the two page-level charts. At 40 instances
 * a chart library costs real per-instance overhead and buys nothing here:
 * there are no axes, no ticks, no tooltips and no legend to get for free. The
 * inconsistency is a decision, not drift.
 *
 * Scaling is per-tenant and zero-anchored — 0 at the floor, this tenant's max
 * at the ceiling — with in and out sharing that one scale. That makes the
 * RATIO between in and out readable: out running at half of in draws at half
 * the height, so a tenant sending half what it receives cannot look like it
 * is keeping up. The classic sparkline alternative, anchoring at min instead
 * of zero, reads within-series wiggle better but destroys exactly this
 * comparison — a 20% gap on tiny numbers would draw floor-to-ceiling,
 * identical to a 99% gap, and a flat high-baseline series would be
 * indistinguishable from a flat zero. This column's job is the ratio, so
 * zero-anchoring is the deliberate trade. Magnitude is invisible either way
 * — a scale shared across 40 tenants would flatten every small tenant into a
 * flat line — so the tooltip carries this tenant's own totals, one hover
 * away.
 */
function Sparkline({ series, width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT }: iSparklineProps) {
    // `downsample` is what gets drawn, so the scale below is derived from
    // this — not from `series.points` — on purpose: a decimated peak in the
    // full series must not silently squash the line that is actually on
    // screen. The tooltip totals further down deliberately sum the FULL
    // series instead, because they claim a 24-hour total, not a total of
    // what happened to survive sampling.
    const points = series ? downsample(series.points, DRAW_POINTS) : [];
    const values = points.flatMap(p => [p.received, p.sent]).filter((v): v is number => v !== undefined);

    if (values.length === 0) {
        // "No history" would be false whenever backlog was measured but
        // throughput was not — there IS history here, just none of the kind
        // this cell draws. Say precisely what is missing.
        return (
            <span
                className="mp-absent"
                title="No throughput measured for this tenant in the last 24 hours"
            >
                no data
            </span>
        );
    }

    // Zero-anchored, not min-anchored: 0 maps to the floor, this tenant's own
    // max to the ceiling. See the component doc comment for why the ratio
    // this buys is worth the within-series wiggle it costs.
    const max = Math.max(...values);
    const span = max || 1;

    const firstAt = points[0].at;
    const lastAt = points[points.length - 1].at;
    const timeSpan = lastAt - firstAt || 1;

    const pad = 1.5;
    // A single point has no time span to place it within, so `at` alone
    // can't say where it belongs — plotting it at x=0 (the fallback below)
    // would read as "24 hours ago" for what is actually the newest and only
    // reading. Centering claims neither oldest nor newest, the only position
    // that isn't a lie when there is no axis to speak of.
    const x = (at: number) => (points.length === 1 ? width / 2 : ((at - firstAt) / timeSpan) * width);
    const y = (v: number) => height - pad - (v / span) * (height - pad * 2);

    // Full series, not the drawn/downsampled `points` above — the label
    // claims a 24-hour total, so it must sum everything that was measured,
    // not just what got sampled for drawing.
    const totalIn = sumMeasured(series?.points.map(p => p.received) ?? []).total;
    const totalOut = sumMeasured(series?.points.map(p => p.sent) ?? []).total;
    const label =
        `24h in ${totalIn === undefined ? "not measured" : totalIn.toLocaleString()}, ` +
        `out ${totalOut === undefined ? "not measured" : totalOut.toLocaleString()}`;

    return (
        <svg
            className="mp-spark"
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={label}
        >
            <title>{label}</title>
            <path
                d={linePath(points, p => p.received, x, y)}
                fill="none"
                className="mp-spark-in"
            />
            <path
                d={linePath(points, p => p.sent, x, y)}
                fill="none"
                className="mp-spark-out"
            />
        </svg>
    );
}

export default Sparkline;
